import clone from 'lodash/clone';

import { keyBy } from 'lodash';
import { camelCase } from 'lib/utils/objectSerializer';
import { User, ODCUser } from 'lib/types/global';
import { UsersAction } from 'lib/store/users/actions';
import {
  DELETE_USER_REQUEST,
  GET_MORE_USERS_REQUEST,
  GET_USERS_REQUEST,
  GET_USERS_SUCCESS,
  GET_MORE_USERS_SUCCESS,
  GET_USER_SUCCESS,
  UPDATE_USER_ROLE_REQUEST,
  UPDATE_USER_ROLE_SUCCESS,
  UPDATE_USER_ROLE_FAILURE,
  RESET_EMPLOYEE_STATE,
  LOGOUT_SUCCESS,
  GET_ODC_USERS_REQUEST,
  GET_ODC_USERS_SUCCESS,
  GET_ODC_USERS_FAILURE,
  RESET_ODC_USERS_STATE,
} from 'lib/store/users/actionTypes';
import formatUser from './format';

export interface UserState {
  loading: boolean;
  users: User[];
  usersById: { [id: string]: User };
  orgId: string;
  branchId: string;
  userCount: number;
  odcUserCount: number;
  employee: User | null;
  odcLoading: boolean;
  odcUsers: ODCUser[];
  odcUsersById: { [id: string]: ODCUser };
}

const initialState: UserState = {
  loading: false,
  users: [],
  usersById: {},
  orgId: '',
  branchId: '',
  userCount: 0,
  odcUserCount: 0,
  employee: null,
  odcLoading: false,
  odcUsers: [],
  odcUsersById: {},
};

export default function userReducer(state = initialState, action: UsersAction) {
  switch (action.type) {
    case DELETE_USER_REQUEST: {
      if (action.extra && action.extra.userId) {
        const userId = action.extra.userId;
        const usersById = clone(state.usersById);
        if (userId in usersById) {
          delete usersById[userId];
        }
        const users = state.users.filter((user) => user.drnId !== userId);
        return {
          ...state,
          users,
          usersById,
        };
      }

      return state;
    }

    case GET_MORE_USERS_REQUEST:
    case GET_USERS_REQUEST: {
      return {
        ...state,
        loading: true,
      };
    }

    case GET_ODC_USERS_REQUEST: {
      return {
        ...state,
        odcLoading: true,
      };
    }

    case GET_USERS_SUCCESS: {
      const { result, total_count: userCount } = action.payload;

      const camelCaseResult = result.map((r) => camelCase(r));
      const usersById = keyBy(camelCaseResult, 'drnId');

      const newState = {
        ...state,
        loading: false,
        users: camelCaseResult,
        usersById,
        userCount,
      };

      return newState;
    }

    case GET_MORE_USERS_SUCCESS: {
      const { result } = action.payload;

      const camelCaseResult = result.map((r) => camelCase(r));
      const users = [...state.users, ...camelCaseResult];
      const usersById = keyBy(users, 'drnId');

      return {
        ...state,
        loading: false,
        users,
        usersById,
      };
    }

    case GET_ODC_USERS_SUCCESS: {
      const newOdcUsers = action.payload.map((r) => camelCase(r));
      const odcUsers = [...state.odcUsers, ...newOdcUsers];
      const odcUsersById = keyBy(odcUsers, 'securityPrincipalId');
      const odcUserCount = odcUsers.length;

      return {
        ...state,
        odcLoading: false,
        odcUsers,
        odcUsersById,
        odcUserCount,
      };
    }

    case GET_ODC_USERS_FAILURE: {
      return {
        ...state,
        odcLoading: false,
      };
    }

    case RESET_ODC_USERS_STATE: {
      return {
        ...state,
        odcUsers: [],
      };
    }

    case GET_USER_SUCCESS: {
      return {
        ...state,
        loading: false,
        employee: formatUser(camelCase(action.payload)),
      };
    }

    case UPDATE_USER_ROLE_REQUEST: {
      return {
        ...state,
        loading: true,
      };
    }

    case UPDATE_USER_ROLE_SUCCESS: {
      const camelCasePayload: User = camelCase(action.payload);
      const users = state.users.reduce<User[]>(
        (accum, user) => [
          ...accum,
          camelCasePayload.id === user.id ? camelCasePayload : user,
        ],
        [],
      );

      const usersById = state.usersById;
      usersById[camelCasePayload.drnId] = camelCasePayload;

      return {
        ...state,
        users,
        usersById,
        loading: false,
      };
    }

    case UPDATE_USER_ROLE_FAILURE: {
      return {
        ...state,
        loading: false,
      };
    }

    case RESET_EMPLOYEE_STATE: {
      return {
        ...state,
        employee: null,
      };
    }

    case LOGOUT_SUCCESS: {
      return initialState;
    }

    default:
      return state;
  }
}

export { initialState };
